import { creatureSprite } from "../art/creatures.ts";
import { hex, mix, UI, withA, type Col } from "../art/pal.ts";
import { STARTERS, SPECIES, SPECIES_IDS, type SpeciesId } from "../data/species.ts";
import { drawText, measureText } from "../engine/font.ts";
import { easeBack, easeOut, SCREEN_H, SCREEN_W, type Gfx } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import { loadSave, newGame, type SaveState } from "../game/save.ts";
import { DISCORD_INVITE, openLink } from "../net/links.ts";
import type { Scene, ScenePayload } from "../game/scenes.ts";
import { focus, glass, hint, meter, typeBadge } from "../ui/kit.ts";
import { actorFrame, PLAYER_OUTFIT } from "../world/actors.ts";
import { bayer, fbm, hash2 } from "../world/noise.ts";
import { tallTuft } from "../world/props.ts";

// ---- Baked layers -----------------------------------------------------------------------------

let sky: ImageData | null = null;
function skyLayer(): ImageData {
  if (sky) return sky;
  const img = new ImageData(SCREEN_W, SCREEN_H);
  const d = img.data;
  const stops: [number, Col][] = [[0, hex("#141339")], [0.35, hex("#3a2a72")], [0.6, hex("#8a4a8f")], [0.78, hex("#ff8a6a")], [0.9, hex("#ffc48a")]];
  const at = (t: number): Col => {
    for (let i = 0; i < stops.length - 1; i++) {
      const [a, ca] = stops[i]!, [b, cb] = stops[i + 1]!;
      if (t <= b) return mix(ca, cb, (t - a) / (b - a));
    }
    return stops[stops.length - 1]![1];
  };
  for (let y = 0; y < SCREEN_H; y++) {
    for (let x = 0; x < SCREEN_W; x++) {
      const t = y / 150 + (bayer(x, y) - 0.5) * 0.025;
      const c = at(Math.min(0.9, t));
      const o = (y * SCREEN_W + x) * 4;
      d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2]; d[o + 3] = 255;
    }
  }
  // Moon.
  for (let y = 4; y < 66; y++) for (let x = 286; x < 348; x++) {
    const dx = x - 317, dy = y - 35;
    const r = Math.hypot(dx, dy);
    const o = (y * SCREEN_W + x) * 4;
    if (r < 13) {
      const crater = fbm(x / 5, y / 5, 2, 9) > 0.62 ? 0.9 : 1;
      d[o] = 255 * crater; d[o + 1] = 244 * crater; d[o + 2] = 214 * crater;
    } else if (r < 30) {
      const k = (1 - (r - 13) / 17) ** 2 * 0.35;
      d[o] = d[o]! + (255 - d[o]!) * k; d[o + 1] = d[o + 1]! + (230 - d[o + 1]!) * k; d[o + 2] = d[o + 2]! + (200 - d[o + 2]!) * k;
    }
  }
  // Far hills.
  const band = (base: number, amp: number, freq: number, seed: number, c: Col, lit: Col) => {
    for (let x = 0; x < SCREEN_W; x++) {
      const top = Math.round(base - fbm(x / freq, seed, 3, seed) * amp);
      for (let y = top; y < SCREEN_H; y++) {
        const o = (y * SCREEN_W + x) * 4;
        const cc = y - top < 2 ? lit : c;
        d[o] = cc[0]; d[o + 1] = cc[1]; d[o + 2] = cc[2];
      }
    }
  };
  band(150, 46, 70, 3, hex("#5a3a7a"), hex("#b06a8a"));
  band(166, 34, 44, 7, hex("#3a2a5e"), hex("#8a5a8a"));
  // Stubtown skyline with lit windows.
  for (let b = 0; b < 12; b++) {
    const bx = 20 + b * 30 + Math.floor(hash2(b, 1, 5) * 10);
    const bw = 18 + Math.floor(hash2(b, 2, 5) * 12);
    const bh = 14 + Math.floor(hash2(b, 3, 5) * 18);
    const by = 176 - bh;
    for (let y = by - 8; y < 190; y++) {
      for (let x = bx - 2; x < bx + bw + 2; x++) {
        if (x < 0 || x >= SCREEN_W) continue;
        const roof = y < by && Math.abs(x - bx - bw / 2) > (y - by + 8) * (bw / 16) + 1;
        if (roof) continue;
        const o = (y * SCREEN_W + x) * 4;
        let c = y < by ? hex("#2a1d48") : hex("#241a40");
        if (y >= by + 3 && (x - bx) % 6 >= 2 && (x - bx) % 6 <= 3 && (y - by) % 7 >= 2 && (y - by) % 7 <= 4 && hash2(x >> 2, y >> 3, b) > 0.35) c = hex("#ffcf70");
        d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2];
      }
    }
  }
  // Foreground meadow.
  for (let x = 0; x < SCREEN_W; x++) {
    const top = Math.round(184 - Math.sin(x / 60) * 6 - fbm(x / 30, 2, 2, 11) * 6);
    for (let y = top; y < SCREEN_H; y++) {
      const o = (y * SCREEN_W + x) * 4;
      const k = y - top;
      const c = k < 1 ? hex("#ff9a7a") : k < 3 ? hex("#3c6a5a") : hash2(x, y, 3) < 0.08 ? hex("#2a5048") : hex("#1f3d3a");
      d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2];
    }
  }
  sky = img;
  return img;
}

let logoImg: ImageData | null = null;
export function logo(): ImageData {
  if (logoImg) return logoImg;
  const text = "POCKET MEME";
  const scale = 4;
  const tw = measureText(text, scale);
  const w = tw + 12, h = 7 * scale + 14;
  const mask = new ImageData(w, h);
  drawText(mask, 6, 4, text, [255, 255, 255, 255], 0, scale);
  const out = new ImageData(w, h);
  const m = (x: number, y: number) => (x >= 0 && y >= 0 && x < w && y < h ? mask.data[(y * w + x) * 4 + 3]! : 0);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const o = (y * w + x) * 4;
      if (m(x, y)) {
        const t = (y - 4) / (7 * scale);
        let c = t < 0.45 ? mix(hex("#fff6c0"), hex("#ffd35a"), t / 0.45) : mix(hex("#ffb23e"), hex("#ff6a3a"), (t - 0.45) / 0.55);
        if (!m(x, y - 1)) c = hex("#ffffff");
        if (!m(x, y + 1)) c = hex("#c2402e");
        out.data[o] = c[0]; out.data[o + 1] = c[1]; out.data[o + 2] = c[2]; out.data[o + 3] = 255;
        continue;
      }
      // Outline (2px) and drop shadow.
      let near = false;
      for (let dy = -2; dy <= 2 && !near; dy++) for (let dx = -2; dx <= 2 && !near; dx++) if (Math.abs(dx) + Math.abs(dy) <= 3 && m(x + dx, y + dy)) near = true;
      if (near) {
        out.data[o] = 44; out.data[o + 1] = 20; out.data[o + 2] = 60; out.data[o + 3] = 255;
      } else if (m(x - 1, y - 5) || m(x, y - 5)) {
        out.data[o] = 30; out.data[o + 1] = 12; out.data[o + 2] = 50; out.data[o + 3] = 150;
      }
    }
  }
  logoImg = out;
  return out;
}

function drawLogo(g: Gfx, cx: number, y: number, t: number, reduced: boolean): void {
  const img = logo();
  const pop = reduced ? 1 : easeBack(Math.min(1, t / 700));
  const bob = reduced ? 0 : Math.round(Math.sin(t / 700) * 2);
  const w = Math.round(img.width * pop), h = Math.round(img.height * pop);
  g.blitScaled(img, Math.round(cx - w / 2), y + bob + Math.round((img.height - h) / 2), w, h);
  if (!reduced && pop >= 1) {
    // Shine sweep across the letters.
    const p = ((t / 2600) % 1.4) * (img.width + 60) - 30;
    const x0 = Math.round(cx - img.width / 2);
    for (let yy = 0; yy < img.height; yy++) {
      for (let k = 0; k < 6; k++) {
        const xx = Math.round(p + k - yy * 0.5);
        if (xx < 0 || xx >= img.width) continue;
        const o = (yy * img.width + xx) * 4;
        if (img.data[o + 3]! < 255 || img.data[o]! < 150) continue;
        g.add(x0 + xx, y + bob + yy, [255, 255, 255, 255], 0.55);
      }
    }
  }
}

function drawScenery(g: Gfx, t: number, reduced: boolean): void {
  g.copyRegion(skyLayer(), 0, 0, SCREEN_W, SCREEN_H, 0, 0);
  // Twinkling stars.
  for (let i = 0; i < 70; i++) {
    const x = Math.floor(hash2(i, 1, 21) * SCREEN_W), y = Math.floor(hash2(i, 2, 21) * 90);
    const tw = reduced ? 0.6 : 0.5 + 0.5 * Math.sin(t / (300 + hash2(i, 3, 21) * 500) + i);
    g.p(x, y, [255, 250, 230, 60 + 180 * tw]);
    if (tw > 0.9 && hash2(i, 4, 21) > 0.7) { g.p(x - 1, y, [255, 250, 230, 90]); g.p(x + 1, y, [255, 250, 230, 90]); g.p(x, y - 1, [255, 250, 230, 90]); g.p(x, y + 1, [255, 250, 230, 90]); }
  }
  // Drifting cloud wisps.
  const drift = reduced ? 0 : t / 90;
  for (let c = 0; c < 5; c++) {
    const cy = 50 + c * 17;
    const cx = ((hash2(c, 5, 8) * 500 + drift * (0.4 + c * 0.15)) % 520) - 70;
    for (let k = 0; k < 5; k++) {
      g.ellipse(cx + k * 13, cy + Math.sin(k) * 2, 14 - Math.abs(k - 2) * 2, 4, [255, 170, 190, 40 + c * 6]);
    }
  }
}

function drawForeground(g: Gfx, t: number, reduced: boolean): void {
  for (let i = 0; i < 26; i++) {
    const x = -6 + i * 16 + Math.round(hash2(i, 1, 4) * 6);
    const frame = reduced ? 0 : Math.floor(t / 280 + i * 0.7) & 3;
    const tuft = tallTuft(i % 4, frame);
    g.blit(tuft.img, x, SCREEN_H - 20 + Math.round(hash2(i, 2, 4) * 4), { tint: [30, 60, 70, 255], tintAmt: 0.45 });
  }
  if (!reduced) {
    for (let i = 0; i < 14; i++) {
      const x = (hash2(i, 1, 6) * SCREEN_W + Math.sin(t / 1300 + i) * 20) % SCREEN_W;
      const y = 150 + hash2(i, 2, 6) * 60 + Math.cos(t / 900 + i * 2) * 8;
      const a = 0.5 + 0.5 * Math.sin(t / 250 + i * 3);
      g.glow(x, y, 5, [255, 240, 140, 255], 0.6 * a);
      g.p(x, y, [255, 255, 210, 255 * a]);
    }
  }
}

export function titleScene(ctx: Ctx): Scene {
  let i = 0;
  let t = 0;
  let pocket: SaveState | null = null;
  let miss = false;
  /** Shown when the browser blocks the Discord tab, so the player can still type the address. */
  let linkNote = false;

  function refresh(): void {
    pocket = loadSave();
    miss = false;
    linkNote = false;
    i = pocket ? 1 : 0;
  }

  /** NEW GAME, CONTINUE when there is a pocket, and the Discord link last. */
  function labels(): string[] {
    return pocket ? ["NEW GAME", "CONTINUE", "DISCORD"] : ["NEW GAME", "DISCORD"];
  }

  function activeSeed(): string {
    if (pocket && i === 1) return pocket.seed;
    return ctx.state.seed;
  }

  return {
    id: "title",
    native: true,
    enter() {
      refresh();
      t = ctx.shot ? 2400 : 0;
      ctx.audio.music("title");
    },
    exit() {},
    update() {
      t += ctx.dt;
      const items = labels().length;
      const up = ctx.input.consume("ArrowUp");
      if (up || ctx.input.consume("ArrowDown")) {
        i = (i + (up ? items - 1 : 1)) % items;
        miss = false;
        linkNote = false;
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.muted = !ctx.audio.muted;
      }
      if (ctx.input.consume("Enter")) {
        if (i === items - 1) {
          ctx.audio.confirm();
          linkNote = !openLink(DISCORD_INVITE);
          return;
        }
        if (i === 0) {
          ctx.audio.confirm();
          ctx.replace("starter");
          return;
        }
        const loaded = loadSave();
        if (!loaded) {
          ctx.audio.fail();
          pocket = null;
          i = 0;
          miss = true;
          return;
        }
        ctx.audio.confirm();
        Object.assign(ctx.state, loaded);
        ctx.rng.state = loaded.rng;
        ctx.replace("overworld");
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      drawScenery(g, t, reduced);

      // Showcase creature on the hill (cycles through the roster).
      const period = 2600;
      const idx = ctx.shot === "title" ? SPECIES_IDS.indexOf("laserpup") : reduced ? 0 : Math.floor(t / period) % SPECIES_IDS.length;
      const sid = SPECIES_IDS[Math.max(0, idx)]!;
      const local = reduced ? period : t % period;
      const inK = easeBack(Math.min(1, local / 380));
      const outK = local > period - 260 ? (local - (period - 260)) / 260 : 0;
      const hop = reduced ? 0 : Math.abs(Math.sin(t / 260)) * 3;
      const img = creatureSprite(sid, "front", 2);
      const sw = Math.round(64 * inK * (1 - outK * 0.6)), sh = Math.round(64 * inK * (1 + outK * 0.4));
      g.ellipse(310, 190, 26, 5, [10, 8, 30, 120], 0.5);
      if (sw > 0 && sh > 0) g.blitScaled(img, 310 - sw / 2, 189 - sh - hop, sw, sh, { alpha: 1 - outK, tint: [255, 200, 170, 255], tintAmt: 0.12 });
      const nameW = measureText(SPECIES[sid].name) + 12;
      glass(g, 310 - nameW / 2, 110, nameW, 14, { r: 6, alpha: 170 * (1 - outK) });
      g.textC(310, 114, SPECIES[sid].name, withA(UI.text, 230 * (1 - outK)));
      // The scout watching from the left.
      const scout = actorFrame("player", PLAYER_OUTFIT, 2, reduced ? 0 : (Math.floor(t / 900) % 5 === 0 ? 1 : 0));
      g.ellipse(78, 191, 11, 2.5, [10, 8, 30, 120], 0.5);
      g.blit(scout, 62, 191 - 51, { scale: 2, tint: [255, 180, 150, 255], tintAmt: 0.1 });

      drawForeground(g, t, reduced);
      drawLogo(g, SCREEN_W / 2, 26, t, reduced);
      g.textC(SCREEN_W / 2, 70, "ONE STREET. LATE SHIFT.", withA(UI.goldHi, 230));
      g.textC(SCREEN_W / 2, 81, "catch memes  ·  battle  ·  trade later", withA(UI.text, 170));

      // Menu.
      const items = labels();
      const mk = reduced ? 1 : easeOut(Math.min(1, (t - 500) / 500));
      items.forEach((label, n) => {
        const w = 120, h = 20;
        const x = Math.round(SCREEN_W / 2 - w / 2), y = 96 + n * 23 + Math.round((1 - mk) * 20);
        const on = i === n;
        g.rrect(x, y + 2, w, h, 8, [0, 0, 0, 90 * mk]);
        g.rrect(x, y, w, h, 8, withA(on ? UI.ink : UI.bg1, 230 * mk));
        g.gradV(x + 1, y + 1, w - 2, h - 2, withA(on ? UI.goldHi : UI.bg3, 240 * mk), withA(on ? UI.gold : UI.bg2, 240 * mk), 7);
        g.textC(SCREEN_W / 2, y + 7, `${on ? ">" : " "}${label}`, on ? UI.paperInk : UI.text);
        if (on && mk >= 1) focus(g, x - 1, y - 1, w + 2, h + 2, t, reduced, 9);
      });
      if (pocket && i === 1) {
        const p = pocket as SaveState;
        const info = `${p.name.slice(0, 8)}  ·  ${p.party.length} crew  ·  ${p.money} $`;
        g.textC(SCREEN_W / 2, 166, info, withA(UI.textDim, 230));
      }
      if (linkNote) g.textC(SCREEN_W / 2, 166, DISCORD_INVITE.replace("https://", ""), UI.goldHi);
      const blink = reduced || (t % 1000) < 700;
      if (miss) g.textC(SCREEN_W / 2, 166, "No pocket.", UI.red);
      else if (blink) {
        const label = i === items.length - 1 ? "Discord" : i === 1 ? "continue" : "new game";
        glass(g, SCREEN_W / 2 - 68, 177, 136, 17, { r: 8, alpha: 170 });
        hint(g, SCREEN_W / 2 - 62, 180, "A", label, "a");
        hint(g, SCREEN_W / 2 + 14, 180, "B", ctx.audio.muted ? "unmute" : "mute", "b");
      }
      g.text(8, SCREEN_H - 11, `SEED ${activeSeed()}`, withA(UI.gold, 200));
      g.textR(SCREEN_W - 8, SCREEN_H - 11, "v0.2 · 2026", withA(UI.textDim, 160));
      if (!reduced && t < 400) g.fill(0, 0, SCREEN_W, SCREEN_H, [6, 6, 16, Math.round(255 * (1 - t / 400))]);
    },
  };
}

const STAT_MAX = 110;

/** The story opens on a new game: the KOL Collective and the neighbor who already followed them. */
export const PROLOGUE = "KOL Collective: A billboard lights up over Stubtown: 'Follow us. Buy what we shill. Hold it forever.' Word is they sell to their own followers at the top: exit liquidity. Your neighbor Remy already followed.";

export function starterScene(ctx: Ctx): Scene {
  let i = 0;
  let t = 0;
  let swapAt = -1000;
  let dirSwap = 1;
  return {
    id: "starter",
    native: true,
    enter(_p?: ScenePayload) {
      i = 0;
      t = ctx.shot ? 2000 : 0;
      ctx.audio.music("title");
    },
    exit() {},
    update() {
      t += ctx.dt;
      if (ctx.input.consume("ArrowLeft")) {
        i = (i + 2) % 3;
        swapAt = t; dirSwap = -1;
        ctx.audio.cursor();
      }
      if (ctx.input.consume("ArrowRight")) {
        i = (i + 1) % 3;
        swapAt = t; dirSwap = 1;
        ctx.audio.cursor();
      }
      if (ctx.input.consume("Escape")) {
        ctx.audio.back();
        ctx.replace("title");
        return;
      }
      if (ctx.input.consume("Enter")) {
        ctx.audio.confirm();
        const pick = STARTERS[i]!;
        const next = newGame(ctx.state.seed, pick);
        Object.assign(ctx.state, next);
        ctx.rng.state = next.rng;
        ctx.save();
        ctx.replace("name", { notice: `${SPECIES[pick].name} joins the crew. Don't make it weird. The street already is.`, followUp: PROLOGUE });
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const sp = SPECIES[STARTERS[i]!];
      const accent = typeAccent(sp.types[0]!);
      g.gradV(0, 0, SCREEN_W, SCREEN_H, mix(accent, hex("#141a2e"), 0.72), hex("#0c0f1e"));
      // Spotlight cone + rotating rays.
      for (let y = 0; y < 150; y++) {
        const half = 18 + y * 0.55;
        g.fill(Math.round(SCREEN_W / 2 - half), y, Math.round(half * 2), 1, withA(mix(accent, UI.white, 0.5), 10 + y * 0.12));
      }
      if (!reduced) {
        for (let k = 0; k < 10; k++) {
          const a = t / 3000 + (k / 10) * Math.PI * 2;
          for (let r = 30; r < 200; r += 3) g.add(SCREEN_W / 2 + Math.cos(a) * r, 96 + Math.sin(a) * r * 0.6, accent, 0.05 * (1 - r / 200));
        }
      }
      g.textShadow(14, 12, "PICK YOUR FIRST JOKE", UI.goldHi, UI.ink);
      g.text(14, 23, "Your partner for the whole late shift.", UI.textDim);

      // Pedestals.
      const since = t - swapAt;
      const swapK = reduced ? 1 : easeOut(Math.min(1, since / 260));
      STARTERS.forEach((id, n) => {
        const rel = ((n - i + 4) % 3) - 1; // -1 left, 0 centre, 1 right
        const x = SCREEN_W / 2 + rel * 112 - (1 - swapK) * dirSwap * 40 * (rel === 0 ? 1 : 0);
        const on = rel === 0;
        const py = on ? 132 : 118;
        g.ellipse(x, py + 4, on ? 44 : 30, on ? 10 : 7, [0, 0, 0, 90], 0.4);
        g.ellipse(x, py, on ? 42 : 28, on ? 9 : 6, on ? mix(accent, UI.white, 0.3) : UI.bg3);
        g.ellipse(x, py - 1, on ? 38 : 25, on ? 7 : 5, on ? mix(accent, UI.white, 0.55) : UI.bg2);
        const scale = on ? 3 : 2;
        const size = 32 * scale;
        const hop = on && !reduced ? Math.abs(Math.sin(t / 300)) * 4 : 0;
        const img = creatureSprite(id, "front", scale);
        g.blit(img, Math.round(x - size / 2), Math.round(py - size + 6 - hop), { tint: on ? undefined : [20, 24, 50, 255], tintAmt: on ? 0 : 0.45 });
        if (!on) g.textC(Math.round(x), py + 12, SPECIES[id].name, UI.textDim);
      });
      g.text(SCREEN_W / 2 - 150, 90, "◀", withA(UI.text, 160));
      g.text(SCREEN_W / 2 + 146, 90, "▶", withA(UI.text, 160));

      // Info card.
      const cx = 12, cy = 150, cw = SCREEN_W - 24, ch = 58;
      glass(g, cx, cy, cw, ch, { r: 7 });
      g.textShadow(cx + 12, cy + 7, sp.name, UI.text, UI.ink, 2);
      let bx = cx + 14 + measureText(sp.name, 2) + 8;
      for (const type of sp.types) bx += typeBadge(g, bx, cy + 9, type) + 3;
      g.para(cx + 12, cy + 26, sp.blurb, 196, UI.textDim, 10, 3);
      const stats: [string, number][] = [["HP", sp.hp], ["ATK", sp.atk], ["DEF", sp.def], ["SPD", sp.spd]];
      stats.forEach(([label, v], n) => {
        const sx = cx + 226, sy = cy + 8 + n * 12;
        g.text(sx, sy, `${label} ${v}`, UI.text);
        meter(g, sx + 44, sy + 1, 84, 6, v / STAT_MAX, { tone: { fill: accent, hi: mix(accent, UI.white, 0.45), lo: mix(accent, UI.black, 0.3) } });
      });
      hint(g, SCREEN_W - 118, 12, "A", "choose", "a");
      hint(g, SCREEN_W - 56, 12, "B", "back", "b");
    },
  };
}

function typeAccent(type: string): Col {
  switch (type) {
    case "cope": return hex("#e8537a");
    case "flex": return hex("#f2a93b");
    case "chaos": return hex("#9b5cf2");
    case "signal": return hex("#35c6e6");
    case "liquid": return hex("#3d7ff0");
    default: return hex("#4fcf62");
  }
}

export type { SpeciesId };
